

export interface ProposalData {
  name: string;
  isPublished: boolean;
  translations: { [key: string]: string};
}

export interface PackagingProposalFilter {
  text: string;
  isTemplate: boolean;
  take: number;
}

export interface Page<T> {
  items: T[];
  hasMoreItems: boolean;
}

export interface PackagingProposalItem {
  id: number;
  name: string;
  product: string;
  isTemplate: boolean;
  customerId: number;
  customerName: string;
  projectId: number;
  projectName: string;
}

export interface ProposalFilter {
  text: string;
  unpublished: boolean;
}

export interface ProposalItem {
  id: number;
  name: string;
  isPublished: boolean;
  packagingsCount: number;
  templatesCount: number;
  createdAt: string;
  changedAt: string;
}

export interface PackagingDescription {
  projectId: number;
  name: string;
  product: string;
  templateId: number | null;
}

export enum RecycabilityCalculation {
  DE2022 = 'DE2022',
  AT2022 = 'AT2022',
  FR2022 = 'FR2022',
  DE2023 = 'DE2023',
  DE2024 = 'DE2024'
}


export enum PackagingStatus {
  Prototype = 'Prototype',
  Model = 'Model',
  Circulation = 'Circulation'
}


export enum ContentType {
  None = 'None',
  Solid = 'Solid',
  Pasty = 'Pasty',
  Liquid = 'Liquid',
  Other = 'Other'
}


export interface PackagingContent {
  type: ContentType;
  volume: number | null;
  mass: number | null;
  isRadioactive: boolean | null;
  isInfectious: boolean | null;
  isOtherHazardous: boolean | null;
  isSealant: boolean | null;
}

export interface PackagingInfoData {
  ownerId: string;
  assumption: string;
  remarks: string;
  observation: string;
  gtin: string;
  articles: string[];
  defaultCalculation: RecycabilityCalculation;
  status: PackagingStatus;
  productTypeId: number | null;
  referenceNr: string;
  isTemplate: boolean;
  customerArticleVersion: string;
  customerArticleNr: string;
  content: PackagingContent;
  classifications: string[];
}

export interface PackagingData {
  description: PackagingDescription;
  parts: PartData[];
  relations: RelationData[];
  image: string;
  images: ImageData[];
  attachments: PackagingAttachmentData[];
  info: PackagingInfoData;
  referencedAsTemplateBy: number[];
  proposals: number[];
}

export enum SortDirection {
  Asc = 'Asc',
  Desc = 'Desc'
}


export interface Sorting<T> {
  property: T;
  direction: SortDirection;
}

export enum PackagingProperty {
  Name = 'Name',
  Product = 'Product',
  CreatedAt = 'CreatedAt',
  ChangedAt = 'ChangedAt',
  Project = 'Project',
  Recycability = 'Recycability'
}


export interface PackagingItemsFilter {
  ownerId: string | null;
  projectId: number | null;
  text: string;
  classifications: string[];
}

export interface PackagingItemsRequest {
  sorting: Sorting<PackagingProperty>;
  filter: PackagingItemsFilter;
  skip: number;
  take: number;
}

export enum RecycabilityClassification {
  C = 'C',
  B = 'B',
  A = 'A',
  Aplus = 'Aplus',
  Aplusplus = 'Aplusplus',
  Aplusplusplus = 'Aplusplusplus',
  Unknown = 'Unknown'
}


export interface PackagingItem {
  projectId: number | null;
  description: PackagingDescription;
  id: number;
  createdAt: string;
  changedAt: string;
  owner: string;
  referencedAsTemplate: boolean;
  recycability: number | null;
  recycabilityClassification: RecycabilityClassification;
}

export interface ChangePasswordData {
  password: string;
  newPassword: string;
}

export interface RecoverPasswordData {
  username: string;
}

export interface ResetPasswordData {
  username: string;
  token: string;
  newPassword: string;
}

export interface TranslationFilter {
  text: string;
  languages: string[];
}

export interface TranslationItem {
  required: boolean;
  values: { [key: string]: string};
}

export interface TranslationData {
  required: boolean;
  parameters: string[];
  values: { [key: string]: string};
}

export interface Void {
}

export interface AdminCustomerData {
  name: string;
  image: string;
  customerCode: string;
  hasProjects: boolean;
  userIds: string[];
}

export interface AdminCustomerItem {
  id: number;
  name: string;
  customerCode: string;
}

export interface AssessmentModel {
  recyclability: number | null;
  scenarios: unknown[];
  proposals: string[];
  date: string;
  customerName: string;
}

export interface UserItem {
  id: string;
  email: string;
  name: string;
  surname: string;
  companyName: string;
  customerIds: number[];
}

export interface TemplateFilter {
  search: string;
  classifications: string[];
}

export interface TemplateItem {
  id: number;
  name: string;
  product: string;
  imageId: number | null;
  components: Text[];
  materials: Text[];
}

export interface MasterData {
  productTypes: { [key: string]: Text};
  classifications: ClassificationData[];
}

export enum ProjectCategory {
  Standard = 'Standard',
  Light = 'Light',
  Silver = 'Silver',
  Gold = 'Gold',
  Combination = 'Combination'
}


export interface ProjectData {
  ownerId: string;
  code: string;
  name: string;
  category: ProjectCategory;
  customerId: number;
  productTypeId: number | null;
  referenceNr: string;
  assumptions: string;
  populateAssumptions: boolean;
  observations: string;
  populateObservations: boolean;
  remarks: string;
  populateRemarks: boolean;
  hasPackagings: boolean;
}

export interface ProjectItem {
  id: number;
  code: string;
  name: string;
  owner: string;
  ownerId: string;
  createdAt: string;
  changedAt: string;
  isCertify: boolean;
}

export enum Component {
  Bag = 'Bag',
  Blister = 'Blister',
  Bottle = 'Bottle',
  Box = 'Box',
  Can = 'Can',
  CapLid = 'CapLid',
  CapOther = 'CapOther',
  CapSpray = 'CapSpray',
  CapTwist = 'CapTwist',
  Clamshell = 'Clamshell',
  Cup = 'Cup',
  Dosifier = 'Dosifier',
  Doypack = 'Doypack',
  Handle = 'Handle',
  Hanger = 'Hanger',
  Insert = 'Insert',
  Jar = 'Jar',
  LabelAdhesive = 'LabelAdhesive',
  LabelInMold = 'LabelInMold',
  LabelLooseSleeve = 'LabelLooseSleeve',
  LabelOther = 'LabelOther',
  LabelShrinkwrap = 'LabelShrinkwrap',
  Pouch = 'Pouch',
  SecuritySeal = 'SecuritySeal',
  SwingTop = 'SwingTop',
  Tie = 'Tie',
  Tray = 'Tray',
  Tube = 'Tube',
  Valve = 'Valve',
  Wrapper = 'Wrapper',
  BeverageBottle = 'BeverageBottle',
  Net = 'Net'
}


export enum MaterialFamily {
  Plastics = 'Plastics',
  Fibers = 'Fibers',
  Metals = 'Metals',
  Glass = 'Glass',
  Composites = 'Composites',
  OtherMaterials = 'OtherMaterials'
}


export enum Material {
  PET = 'PET',
  PETWithBarrierAdditive = 'PETWithBarrierAdditive',
  PETG = 'PETG',
  PEHD = 'PEHD',
  PELD = 'PELD',
  PEX = 'PEX',
  PP = 'PP',
  PS = 'PS',
  PSE = 'PSE',
  PA = 'PA',
  POM = 'POM',
  PC = 'PC',
  PVC = 'PVC',
  PVDC = 'PVDC',
  PBS = 'PBS',
  PLA = 'PLA',
  ABS = 'ABS',
  PMMA = 'PMMA',
  ElastomerRubber = 'ElastomerRubber',
  ElastomerThermoplastic = 'ElastomerThermoplastic',
  EVOH = 'EVOH',
  Silicone = 'Silicone',
  EVA = 'EVA',
  WaxParaffine = 'WaxParaffine',
  PAWithException = 'PAWithException',
  PSO = 'PSO',
  Cardboard = 'Cardboard',
  CardboardWetResistant = 'CardboardWetResistant',
  MouldedPaper = 'MouldedPaper',
  MouldedPaperWetResistant = 'MouldedPaperWetResistant',
  Paper = 'Paper',
  PaperWetResistant = 'PaperWetResistant',
  Paperboard = 'Paperboard',
  PaperboardWetResistant = 'PaperboardWetResistant',
  Aluminium = 'Aluminium',
  FE = 'FE',
  AlOx = 'AlOx',
  SodaLimeGlass = 'SodaLimeGlass',
  SiOx = 'SiOx',
  OtherGlass = 'OtherGlass',
  Ceramic = 'Ceramic',
  Natural = 'Natural',
  Superabsorber = 'Superabsorber',
  MultilayerPlastic = 'MultilayerPlastic',
  FiberWithOthers = 'FiberWithOthers',
  BeverageCarton = 'BeverageCarton',
  OtherComposite = 'OtherComposite'
}


export enum DensityModification {
  None = 'None',
  Foamt = 'Foamt',
  Filled = 'Filled'
}


export enum Color {
  Any = 'Any',
  AnyColorButBlack = 'AnyColorButBlack',
  Black = 'Black',
  BlueTransparent = 'BlueTransparent',
  BrownTransparent = 'BrownTransparent',
  ClearTransparent = 'ClearTransparent',
  ClearColorful = 'ClearColorful',
  ClearWithDirectPrinting = 'ClearWithDirectPrinting',
  GreenTransparent = 'GreenTransparent',
  HolograficPrinting = 'HolograficPrinting',
  Kraft = 'Kraft',
  MetallizedPrinting = 'MetallizedPrinting',
  NaturalClear = 'NaturalClear',
  Opaque = 'Opaque',
  OtherColor = 'OtherColor',
  PrintedBlack = 'PrintedBlack',
  PrintedDarkColors = 'PrintedDarkColors',
  PrintedLightColors = 'PrintedLightColors',
  White = 'White',
  BlackNIRDetectable = 'BlackNIRDetectable',
  BlackNotSootBased = 'BlackNotSootBased',
  PrintedBlackNotSootBased = 'PrintedBlackNotSootBased'
}


export enum AdhesiveType {
  None = 'None',
  SolubleAdhesive = 'SolubleAdhesive',
  NonSolubleAdhesive = 'NonSolubleAdhesive'
}


export enum AdhesiveApplication {
  Areal = 'Areal',
  Selective = 'Selective'
}


export interface Adhesive {
  type: AdhesiveType;
  fraction: number | null;
  application: AdhesiveApplication;
}

export enum FinishingType {
  None = 'None',
  VapourDeposition = 'VapourDeposition',
  Lacquering = 'Lacquering',
  Embossing = 'Embossing',
  PolymericThermoplasticDispersionCoating = 'PolymericThermoplasticDispersionCoating'
}


export interface Finishing {
  type: FinishingType;
  surface: number;
  containsMetal: boolean;
  isTransparent: boolean;
  thickness: number | null;
}

export enum Shape {
  Round = 'Round',
  Square = 'Square'
}


export interface PartData {
  id: string;
  name: string;
  component: Component;
  materialFamily: MaterialFamily;
  material: Material;
  densityModification: DensityModification;
  materialDensityLow: number | null;
  materialDensityHigh: number | null;
  color: Color;
  ncBasedPrinting: boolean | null;
  composites: CompositeData[];
  adhesive: Adhesive;
  finishing: Finishing;
  shape: Shape;
  x: number;
  y: number;
  z: number;
  weight: number;
}

export enum SeparationProbability {
  Never = 'Never',
  Rarely = 'Rarely',
  Occasionally = 'Occasionally',
  Often = 'Often',
  VeryOften = 'VeryOften'
}


export interface RelationData {
  fromId: string;
  toId: string;
  separationProbability: SeparationProbability;
  combinationPackaging: boolean;
}

export interface ImageData {
  value: string;
  tagline: string;
}

export interface PackagingAttachmentData {
  filename: string;
  value: string;
  tagline: string;
}

export type Text =
  | { kind: "Value"; value: string; }
  | { kind: "Translation"; key: string; args?: Record<string, Text | null>; }
  | { kind: "List"; items: (Text | null)[]; };

export enum ClassificationType {
  Group = 'Group',
  Classification = 'Classification'
}


export interface ClassificationData {
  id: string;
  type: ClassificationType;
  name: Text;
  children: ClassificationData[];
}

export interface CompositeData {
  id: string;
  material: Material;
  fraction: number;
}